// Analytics facade — the ONLY analytics module the rest of the app imports.
//
// Vendor-agnostic by construction: callers speak in stable event names and
// these helpers; the concrete backend (PostHog today) is chosen at runtime
// from GET /api/config and hidden behind the AnalyticsProvider interface.
// Swapping vendors means adding a provider under ./providers/ and a branch
// in `providerFor` below — no call site changes.
//
// Two invariants every helper upholds, so call sites never need their own
// guards:
//   1. No-op until a provider has successfully initialized.
//   2. Never throws — analytics must not be able to break the app.
//
// PII policy: event *properties* are NOT masked the way session-replay text
// is. Keep payloads to ids, counts, enums, and booleans — never chat
// content, entity names, document text, or emails. (Identity is limited to
// the OIDC `sub` in the provider's identify().)

import type { AnalyticsProvider, ClientConfig } from './types';
import { NoopProvider } from './providers/noop';
import { PostHogProvider } from './providers/posthog';
import type { CurrentUser } from '../stores/authStore';

// Becomes a real provider only after a successful init(). Until then it's
// the no-op, so every helper below can delegate unconditionally.
let provider: AnalyticsProvider = new NoopProvider();
let initialized = false;

/** Map a config `provider` tag to a concrete implementation. */
function providerFor(tag: string): AnalyticsProvider | null {
    switch (tag) {
        case 'posthog':
            return new PostHogProvider();
        case 'none':
            return null;
        default:
            // Unknown provider tag — fail safe to no analytics rather than
            // guess. (Keeps an old frontend forward-compatible with a config
            // that names a provider it doesn't know yet.)
            return null;
    }
}

/**
 * Fetch runtime config and initialize the selected analytics provider.
 *
 * Safe to call unconditionally on boot: if analytics is disabled, the
 * provider is unknown, or the config fetch fails, this resolves quietly
 * with the no-op provider still in place. Never throws — analytics must
 * never be able to break app startup.
 */
export async function initAnalytics(): Promise<void> {
    if (initialized) return;
    try {
        const res = await fetch('/api/config');
        if (!res.ok) return;
        const config = (await res.json()) as ClientConfig;
        const analytics = config?.analytics;
        if (!analytics) return;

        const selected = providerFor(analytics.provider);
        if (!selected) return;

        await selected.init(analytics);
        provider = selected;
        initialized = true;
    } catch {
        // Swallow — a missing/broken config endpoint or network error must
        // not block the app. Analytics simply stays off (no-op provider).
    }
}

/**
 * Tie subsequent events to the signed-in user. Called when the auth store
 * learns who the user is (after /auth/me). PII policy (only the OIDC `sub`,
 * no name/email) lives in the provider.
 */
export function identifyUser(user: CurrentUser): void {
    if (!initialized) return;
    provider.identify(user);
}

/**
 * Drop the identity link on logout so the next user on this browser starts
 * a fresh session rather than inheriting the previous distinct id.
 */
export function resetAnalytics(): void {
    if (!initialized) return;
    provider.reset();
}

/**
 * Capture a custom product event. No-op until analytics is initialized, so
 * call sites don't need their own guards. Keep `properties` to ids, counts,
 * enums, and booleans — never user content (see PII policy above).
 */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
    if (!initialized) return;
    provider.capture(event, properties);
}

/**
 * Record a client-side route change. SPA navigations aren't real page
 * loads, so the router calls this explicitly on each navigation.
 */
export function trackPage(name: string, properties?: Record<string, unknown>): void {
    if (!initialized) return;
    provider.page(name, properties);
}
